import React, { useEffect, useState } from 'react';
import Layout from './Layout';
import { defaultAxiosInstance } from '../api/defaultInstance';
import { useUser } from '../context/UserContext';
import '../css/AdSettlementManagement.css';

function AdSettlementManagement() {
    const { userInfo } = useUser();
    // data-scope: D-14 — partner는 자기 데이터만 보는 표시·필터 범위다(기능 권한 판단 아님, 권한 시스템 범위 밖)
    const isPartnerScope = userInfo?.role === 'partner'; // data-scope: D-14
    const [isLoading, setIsLoading] = useState(false);
    const [list, setList] = useState([]);
    const [summary, setSummary] = useState({
        total_reward_10: 0,
        total_reward_5_root: 0,
        total_reward_5_parent: 0,
        total_reward_1: 0,
        total_reward_membership: 0,
        total_reward_sum: 0
    });
    const [filter, setFilter] = useState({
        startDate: '',
        endDate: '',
        name: '',
        parentUserName: '',
        membership: true,
        reward10: true,
        reward5: true,
        reward1: true,
        page: 1,
        limit: 20
    });
    const [totalCount, setTotalCount] = useState(0);

    // 데이터 조회
    const fetchData = async (currentFilter = filter) => {
        setIsLoading(true);
        try {
            const params = {
                page: currentFilter.page || 1,
                limit: currentFilter.limit || 20
            };

            if (currentFilter.startDate) params.startDate = currentFilter.startDate;
            if (currentFilter.endDate) params.endDate = currentFilter.endDate;
            if (currentFilter.name) params.name = currentFilter.name;
            if (currentFilter.parentUserName) params.parentUserName = currentFilter.parentUserName;
            if (currentFilter.membership) params.membership = true;
            if (currentFilter.reward10) params.reward10 = true;
            if (currentFilter.reward5) params.reward5 = true;
            if (currentFilter.reward1) params.reward1 = true;

            const response = await defaultAxiosInstance.get('/advertisement/admin/settlement-management', {
                params
            });

            if (response.data.status === 200) {
                const data = response.data.data;
                setList(data.list || []);
                setTotalCount(data.totalCount || 0);
                setSummary(data.summary || {
                    total_reward_10: 0,
                    total_reward_5_root: 0,
                    total_reward_5_parent: 0,
                    total_reward_1: 0,
                    total_reward_membership: 0,
                    total_reward_sum: 0
                });
            }
        } catch (error) {
            console.error('데이터 조회 오류:', error);
            alert('데이터 조회 중 오류가 발생했습니다.');
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        // 초기 날짜 설정 (이번 달 1일)
        const now = new Date();
        const firstDayOfMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`;
        const today = new Date().toISOString().split('T')[0];
        const initialFilter = {
            startDate: firstDayOfMonth,
            endDate: today,
            name: '',
            parentUserName: '',
            membership: true,
            reward10: true,
            reward5: true,
            reward1: true,
            page: 1,
            limit: 20
        };
        setFilter(initialFilter);
        // 초기 데이터 조회
        fetchData(initialFilter);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userInfo]);

    useEffect(() => {
        if (filter.startDate && filter.endDate) {
            fetchData();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filter.page]);

    const handleFilterChange = (e) => {
        const { name, value, type, checked } = e.target;
        if (type === 'checkbox') {
            setFilter(prev => ({ ...prev, [name]: checked }));
        } else {
            setFilter(prev => ({ ...prev, [name]: value }));
        }
    };

    const applyFilter = () => {
        const filterToApply = { ...filter, page: 1 };
        setFilter(prev => ({ ...prev, page: 1 }));
        fetchData(filterToApply);
    };

    const handlePrevPage = () => {
        const newPage = Math.max(1, filter.page - 1);
        const updatedFilter = { ...filter, page: newPage };
        setFilter(updatedFilter);
        fetchData(updatedFilter);
    };

    const handleNextPage = () => {
        const newPage = filter.page + 1;
        const updatedFilter = { ...filter, page: newPage };
        setFilter(updatedFilter);
        fetchData(updatedFilter);
    };

    const handlePageClick = (page) => {
        const updatedFilter = { ...filter, page };
        setFilter(updatedFilter);
        fetchData(updatedFilter);
    };

    const startIndex = (filter.page - 1) * filter.limit + 1;
    const endIndex = Math.min(filter.page * filter.limit, totalCount);
    const totalPages = Math.ceil(totalCount / filter.limit);

    return (
        <Layout>
            <div className="ad-settlement-management-container">
                {/* 요약 카드 */}
                <div className={`summary ${isPartnerScope ? 'partner-summary' : ''}`}>
                    <div className="summary-box">
                        정산 예정 총액<br />
                        <strong>{summary.total_reward_sum?.toLocaleString() || 0}원</strong>
                    </div>
                    <div className="summary-box">
                        멤버십 정산액<br />
                        <strong>{summary.total_reward_membership?.toLocaleString() || 0}원</strong>
                    </div>
                    <div className="summary-box">
                        10% 적립액<br />
                        <strong>{summary.total_reward_10?.toLocaleString() || 0}원</strong>
                    </div>
                    <div className="summary-box">
                        강사 5% 적립액<br />
                        <strong>{summary.total_reward_5_root?.toLocaleString() || 0}원</strong>
                    </div>
                    {!isPartnerScope && (
                        <div className="summary-box">
                            강사 외 5% 적립액<br />
                            <strong>{summary.total_reward_5_parent?.toLocaleString() || 0}원</strong>
                        </div>
                    )}
                    <div className="summary-box">
                        내가 쓴 적립액<br />
                        <strong>{summary.total_reward_1?.toLocaleString() || 0}원</strong>
                    </div>
                </div>

                {/* 필터 섹션 */}
                <div className="filter-bar">
                    <div className="filter-group">
                        <label>조회 기간 설정</label>
                        <div className="date-range">
                            <input
                                type="checkbox"
                                name="allPeriod"
                                id="allPeriod"
                                checked={!filter.startDate && !filter.endDate}
                                onChange={(e) => {
                                    if (e.target.checked) {
                                        setFilter(prev => ({ ...prev, startDate: '', endDate: '' }));
                                    } else {
                                        const now = new Date();
                                        const firstDayOfMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`;
                                        const today = new Date().toISOString().split('T')[0];
                                        setFilter(prev => ({ ...prev, startDate: firstDayOfMonth, endDate: today }));
                                    }
                                }}
                            />
                            <label htmlFor="allPeriod">전체</label>
                            <input
                                type="date"
                                name="startDate"
                                value={filter.startDate}
                                onChange={handleFilterChange}
                                disabled={!filter.startDate && !filter.endDate}
                            />
                            <span>~</span>
                            <input
                                type="date"
                                name="endDate"
                                value={filter.endDate}
                                onChange={handleFilterChange}
                                disabled={!filter.startDate && !filter.endDate}
                            />
                        </div>
                    </div>
                    {!isPartnerScope && (
                    <div className="filter-group">
                        <label>이름</label>
                        <input
                            type="text"
                            name="name"
                            placeholder="이름 입력"
                            value={isPartnerScope 
                                ? (userInfo?.name || '')
                                : filter.name}
                            onChange={handleFilterChange}
                            onKeyDown={e => { if (e.key === 'Enter') applyFilter(); }}
                        />
                    </div>
                    )}
                    {!isPartnerScope && (
                    <div className="filter-group">
                        <label>상위 또는 최상위 유저</label>
                        <input
                            type="text"
                            name="parentUserName"
                            placeholder="유저명"
                            value={filter.parentUserName}
                            onChange={handleFilterChange}
                            onKeyDown={e => { if (e.key === 'Enter') applyFilter(); }}
                        />
                    </div>
                    )}
                    <div className={`filter-group ${!isPartnerScope ? 'filter-group-span-2' : ''}`}>
                        <label>적립 유형</label>
                        <div className="reward-type-checkboxes">
                            <label>
                                <input
                                    type="checkbox"
                                    name="membership"
                                    checked={filter.membership}
                                    onChange={handleFilterChange}
                                />
                                멤버십
                            </label>
                            <label>
                                <input
                                    type="checkbox"
                                    name="reward10"
                                    checked={filter.reward10}
                                    onChange={handleFilterChange}
                                />
                                10% 적립
                            </label>
                            <label>
                                <input
                                    type="checkbox"
                                    name="reward5"
                                    checked={filter.reward5}
                                    onChange={handleFilterChange}
                                />
                                5% 적립
                            </label>
                            <label>
                                <input
                                    type="checkbox"
                                    name="reward1"
                                    checked={filter.reward1}
                                    onChange={handleFilterChange}
                                />
                                1% 적립
                            </label>
                        </div>
                    </div>
                    <div className="filter-group query-button-group">
                        <button onClick={applyFilter} className="query-button">
                            조회
                        </button>
                    </div>
                </div>

                {/* 테이블 */}
                <div className="table-container">
                    <table>
                        <thead>
                            <tr>
                                <th>이름</th>
                                <th>멤버십 적립액</th>
                                <th>10% 적립액</th>
                                <th>강사 5% 적립액</th>
                                {!isPartnerScope && <th>사용자 5% 적립액</th>}
                                <th>1% 적립액</th>
                                <th>기간 총액</th>
                                <th>상위 유저</th>
                                <th>최상위 유저</th>
                            </tr>
                        </thead>
                        <tbody>
                            {isLoading ? (
                                <tr>
                                    <td colSpan="9" className="center-cell">로딩 중...</td>
                                </tr>
                            ) : list.length === 0 ? (
                                <tr>
                                    <td colSpan="9" className="center-cell">데이터가 없습니다.</td>
                                </tr>
                            ) : (
                                list.map((item, idx) => (
                                    <tr key={item.user_id || idx}>
                                        <td>{item.name || ''}</td>
                                        <td>{item.reward_membership?.toLocaleString() || 0}원</td>
                                        <td>{item.reward_10?.toLocaleString() || 0}원</td>
                                        <td>{item.reward_5_root?.toLocaleString() || 0}원</td>
                                        {!isPartnerScope && <td>{item.reward_5_parent?.toLocaleString() || 0}원</td>}
                                        <td>{item.reward_1?.toLocaleString() || 0}원</td>
                                        <td>{item.reward_sum?.toLocaleString() || 0}원</td>
                                        <td>{item.parent_user_name || '-'}</td>
                                        <td>{item.root_user_name || '-'}</td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {/* 페이징 */}
                <div className="pagination-info">
                    <div>총 {totalCount}개 항목 중 {startIndex} - {endIndex}</div>
                    <div className="pagination">
                        <button onClick={handlePrevPage} disabled={filter.page === 1 || isLoading}>이전</button>
                        {totalPages <= 10 ? (
                            Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                                <button
                                    key={page}
                                    className={filter.page === page ? "active" : ""}
                                    onClick={() => handlePageClick(page)}
                                    disabled={isLoading}
                                >
                                    {page}
                                </button>
                            ))
                        ) : (
                            <>
                                {filter.page > 1 && <button onClick={() => handlePageClick(1)} disabled={isLoading}>1</button>}
                                {filter.page > 2 && <span>...</span>}
                                {filter.page > 0 && <button className="active" disabled>{filter.page}</button>}
                                {filter.page < totalPages - 1 && <span>...</span>}
                                {filter.page < totalPages && <button onClick={() => handlePageClick(totalPages)} disabled={isLoading}>{totalPages}</button>}
                            </>
                        )}
                        <button onClick={handleNextPage} disabled={endIndex >= totalCount || isLoading}>다음</button>
                    </div>
                </div>
            </div>
        </Layout>
    );
}

export default AdSettlementManagement;

